import generateSilenceWavBase64 from "@/lib/generateSilenceWavBase64";
// import { cn } from "@/lib/utils"; // not used in this file (kept for parity with related components)
import { ChatStore } from "@/stores/ChatStore";
import { EMicStatus } from "@/types/TMicStatus";
import { useCallback, useEffect, useMemo, useState } from "react";

import MicButton from "./mic-button";
import InputModeToggle from "./input-mode-toggle";
import SettingsStore from "@/stores/SettingsStore";

type TMicHandlerButtonsProps = {
  addUserAudio: (base64Audio: string) => void;
};

export default function MicHandlerButtons({ addUserAudio }: TMicHandlerButtonsProps) {
  const [isFocused, setIsFocused] = useState(false);
  const micStatus = ChatStore(state => state.micStatus);
  const setMicStatus = ChatStore(state => state.setMicStatus);
  const setAvatarStatus = ChatStore(state => state.setAvatarStatus);
  const inputMode = ChatStore(state => state.inputMode);
  const setInputMode = ChatStore(state => state.setInputMode);
  const setIsHistoryPanelOpen = ChatStore(state => state.setIsHistoryPanelOpen);
  const usePushToTalk = SettingsStore(state => state.usePushToTalk);
  const isRecording = useMemo(() => micStatus === EMicStatus.recording, [micStatus]);
  const isMicDisabled = useMemo(() => micStatus === EMicStatus.disabled, [micStatus]);

  const startRecording = useCallback(() => {
    if (inputMode === "text") return; // disable mic in text mode
    setMicStatus(EMicStatus.recording);
    setAvatarStatus("Anhören"); // Using the proper TAvatarStatus type value
  }, [setMicStatus, setAvatarStatus, inputMode]);

  const stopRecording = useCallback(() => {
    if (!isRecording && !isMicDisabled) return;
    setMicStatus(EMicStatus.enabled);

    const silenceBase64 = generateSilenceWavBase64();
    addUserAudio(silenceBase64);
  }, [isRecording, isMicDisabled, setMicStatus, setAvatarStatus, addUserAudio]);

  useEffect(() => {
    const startRecordingOnSpace = (event: KeyboardEvent) => (event?.key === " " && isFocused ? startRecording() : null);

    window.addEventListener("keydown", startRecordingOnSpace);
    window.addEventListener("keyup", stopRecording);
    return () => {
      window.removeEventListener("keydown", startRecordingOnSpace);
      window.removeEventListener("keyup", stopRecording);
    };
  }, [isFocused, startRecording, stopRecording]);

  const toggleInputMode = useCallback(() => {
    if (inputMode === "text") {
      setInputMode("voice");
      setMicStatus(EMicStatus.enabled);
    } else {
      setInputMode("text");
      setIsHistoryPanelOpen(true);
      setMicStatus(EMicStatus.disabled);
    }
  }, [inputMode, setInputMode, setMicStatus]);

  return (
    <div className={"relative flex items-end"}>
      {usePushToTalk &&
        <MicButton
          onStart={startRecording}
          onStop={stopRecording}
          onFocusChange={(f) => setIsFocused(f)}
        />
      }
      <InputModeToggle inputMode={inputMode} onToggle={toggleInputMode} />
    </div>
  );
}
